"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "@frontend/lib/navigation";
import { switchAccountAction } from "@frontend/lib/actions";
import { Avatar, cx } from "@frontend/components/ui";
import { creatorAvatarUrl } from "@frontend/lib/avatar";
import type { AccessibleAccount } from "@creators/backend/lib/access/members";
import { ROLE_LABELS, type MemberRole } from "@creators/backend/lib/access/roles";

type Entry = {
  /** Null is the viewer's own identity, for someone who owns no creator account. */
  creatorId: string | null;
  label: string;
  role: MemberRole | null;
  hint: string;
  avatarUrl: string | null;
};

/**
 * Picks which creator account the session is looking at. Every scoped read on the
 * server keys off the session's active account, so a switch changes the whole app
 * at once — which is why it navigates rather than swapping state in place.
 */
export function AccountSwitcher({
  accounts,
  activeCreatorId,
  personalLabel,
}: {
  accounts: AccessibleAccount[];
  activeCreatorId: string | null;
  personalLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const owned = accounts.find((account) => account.own);

  const entries: Entry[] = [
    ...(owned
      ? []
      : [
          {
            creatorId: null,
            label: personalLabel,
            role: null,
            hint: "Your own collabs",
            avatarUrl: null,
          },
        ]),
    ...accounts.map((account) => ({
      creatorId: account.creatorId,
      label: account.displayName,
      role: account.role,
      hint: account.own
        ? "Your account"
        : account.handle
          ? `@${account.handle}`
          : "Shared with you",
      avatarUrl: creatorAvatarUrl(account.creatorId, account.avatarKey),
    })),
  ];

  // Nothing to switch between: the nav stays as it was before team access existed.
  if (entries.length < 2) return null;

  const active = entries.find((entry) => entry.creatorId === activeCreatorId) ?? entries[0]!;
  const delegated = Boolean(active.role && active.role !== "owner");

  const select = async (entry: Entry) => {
    if (entry.creatorId === activeCreatorId) {
      setOpen(false);
      return;
    }

    setPendingId(entry.creatorId ?? "personal");
    setError(null);

    const formData = new FormData();
    formData.set("creatorId", entry.creatorId ?? "personal");
    // Staying put would land on a collab that belongs to the account just left, so
    // collab pages hand the switch back to the list instead.
    formData.set("next", pathname.startsWith("/collabs/") ? "/collabs" : pathname);

    const result = await switchAccountAction({}, formData);
    // A successful switch navigates, so reaching here means it did not.
    if (result.error) {
      setError(result.error);
      setPendingId(null);
    }
  };

  return (
    <div ref={rootRef} className="relative min-w-0 max-w-[9.5rem] sm:max-w-[14rem]">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Current account: ${active.label}. Switch account`}
        className={cx(
          "flex w-full min-w-0 items-center gap-2 rounded-lg border py-2 pr-2 pl-2.5 text-sm transition",
          delegated
            ? "border-warn-100 bg-warn-50 hover:bg-warn-100/60"
            : "border-line hover:bg-canvas",
        )}
      >
        <Avatar name={active.label} url={active.avatarUrl} size="sm" className="!size-5" />
        <span className="min-w-0 truncate font-medium text-ink">{active.label}</span>
        {delegated ? (
          <span className="hidden shrink-0 text-[10px] font-semibold tracking-wide text-warn-700 uppercase sm:inline">
            {ROLE_LABELS[active.role!]}
          </span>
        ) : null}
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className={cx("size-4 shrink-0 text-ink-subtle transition", open && "rotate-180")}
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-2 w-[min(18rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-line-strong bg-surface py-1 shadow-[0_8px_24px_-10px_rgba(15,18,24,0.18)]"
        >
          <p className="px-3 py-2 text-[10px] font-semibold tracking-wide text-ink-subtle uppercase">
            Switch account
          </p>

          {entries.map((entry) => {
            const key = entry.creatorId ?? "personal";
            const isActive = entry.creatorId === activeCreatorId;

            return (
              <button
                key={key}
                type="button"
                role="menuitem"
                onClick={() => void select(entry)}
                disabled={pendingId !== null}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition hover:bg-canvas disabled:opacity-60"
              >
                <Avatar name={entry.label} url={entry.avatarUrl} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink">{entry.label}</span>
                  <span className="block truncate text-xs text-ink-subtle">
                    {entry.role && entry.role !== "owner"
                      ? `${ROLE_LABELS[entry.role]} · ${entry.hint}`
                      : entry.hint}
                  </span>
                </span>

                {pendingId === key ? (
                  <span className="shrink-0 text-xs text-ink-subtle">…</span>
                ) : isActive ? (
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-4 shrink-0 text-money-600"
                    aria-label="Current account"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : null}
              </button>
            );
          })}

          {error ? (
            <p className="border-t border-line/70 px-3 py-2 text-xs text-alert-600">{error}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
